/**
 * AOS-PEO-03 — Pending Leave Approvals Queue
 *
 * For HR Admins, Deans, and Department Heads.
 * Displays all SUBMITTED leave requests with approve / reject actions.
 * Reuses PEO-02 approval endpoints; no self-approval is permitted.
 */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Clock, CheckCircle2, XCircle, AlertCircle, Filter,
  Search, Users, CalendarDays, RefreshCw, ChevronDown,
  TrendingDown, TrendingUp, Building2, FileText,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

function fmtDt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return d; }
}

function pluralDays(n) {
  return `${n} day${n !== 1 ? "s" : ""}`;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, color = "slate", sub }) {
  const colors = {
    amber:   "bg-amber-50  border-amber-200  text-amber-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red:     "bg-red-50    border-red-200    text-red-700",
    slate:   "bg-slate-50  border-slate-200  text-slate-700",
    blue:    "bg-blue-50   border-blue-200   text-blue-700",
  };
  return (
    <div className={`rounded-xl border p-4 flex gap-3 items-start ${colors[color]}`}
         data-testid={`stat-card-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="mt-0.5 shrink-0"><Icon className="h-5 w-5" strokeWidth={1.75} /></div>
      <div>
        <div className="text-2xl font-bold leading-none">{value ?? "—"}</div>
        <div className="text-xs font-medium mt-0.5 opacity-80">{label}</div>
        {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function BalancePill({ entitled, consumed, available }) {
  if (entitled == null) return <span className="text-xs text-muted-foreground">—</span>;
  const pct = entitled > 0 ? Math.round((available / entitled) * 100) : 0;
  const color = pct >= 50 ? "text-emerald-600" : pct >= 25 ? "text-amber-600" : "text-red-600";
  return (
    <div className="text-xs leading-tight">
      <span className={`font-semibold ${color}`}>{available}d left</span>
      <span className="text-muted-foreground"> / {entitled}d</span>
    </div>
  );
}

function RejectModal({ request, onConfirm, onCancel, loading }) {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-2xl border border-border w-full max-w-md mx-4 p-6"
           data-testid="reject-modal">
        <h3 className="text-lg font-semibold text-foreground mb-1">Reject Leave Request</h3>
        <p className="text-sm text-muted-foreground mb-4">
          You are rejecting <strong>{request?.employee_name}</strong>'s{" "}
          {request?.leave_type_name} request ({request?.from_date} → {request?.to_date}).
          A reason is mandatory.
        </p>
        <textarea
          className="w-full rounded-xl border border-border bg-muted/30 px-3 py-2 text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Enter rejection reason…"
          value={reason}
          onChange={e => setReason(e.target.value)}
          data-testid="reject-reason-input"
          autoFocus
        />
        <div className="flex gap-2 mt-4 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-xl text-sm border border-border hover:bg-muted transition"
            data-testid="reject-modal-cancel"
          >Cancel</button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={!reason.trim() || loading}
            className="px-4 py-2 rounded-xl text-sm bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 transition"
            data-testid="reject-modal-confirm"
          >
            {loading ? "Rejecting…" : "Confirm Rejection"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function PendingApprovals() {
  const token = localStorage.getItem("academiaos_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [stats, setStats]           = useState(null);
  const [pending, setPending]       = useState([]);
  const [filters, setFilters]       = useState({ departments: [], leave_types: [] });
  const [filterDept, setFilterDept] = useState("");
  const [filterLt, setFilterLt]     = useState("");
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionSuccess, setActionSuccess] = useState("");
  const [actionError, setActionError]     = useState("");

  // Reject modal state
  const [rejectTarget, setRejectTarget] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filterDept) params.dept_id = filterDept;
      if (filterLt) params.leave_type_code = filterLt;

      const [statsRes, pendingRes, filtersRes] = await Promise.all([
        axios.get(`${API}/api/v1/peo03/approvals/stats`, { headers }),
        axios.get(`${API}/api/v1/peo03/approvals/pending`, { headers, params }),
        axios.get(`${API}/api/v1/peo03/approvals/filters`, { headers }),
      ]);
      setStats(statsRes.data);
      setPending(pendingRes.data);
      setFilters(filtersRes.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(typeof msg === "object" ? JSON.stringify(msg) : msg);
    } finally {
      setLoading(false);
    }
  }, [filterDept, filterLt]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleApprove = async (requestId) => {
    setActionLoading(true);
    setActionError("");
    setActionSuccess("");
    try {
      await axios.post(
        `${API}/api/v1/peo02/leave/requests/${requestId}/approve`,
        { remarks: "Approved via Pending Approvals queue" },
        { headers },
      );
      setActionSuccess("Leave request approved successfully.");
      loadData();
    } catch (err) {
      const d = err.response?.data?.detail;
      setActionError(typeof d === "object" ? JSON.stringify(d) : (d || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async (reason) => {
    if (!rejectTarget) return;
    setActionLoading(true);
    setActionError("");
    setActionSuccess("");
    try {
      await axios.post(
        `${API}/api/v1/peo02/leave/requests/${rejectTarget.request_id}/reject`,
        { reason },
        { headers },
      );
      setActionSuccess("Leave request rejected.");
      setRejectTarget(null);
      loadData();
    } catch (err) {
      const d = err.response?.data?.detail;
      setActionError(typeof d === "object" ? JSON.stringify(d) : (d || err.message));
    } finally {
      setActionLoading(false);
    }
  };

  // Client-side search filter
  const displayed = pending.filter(item => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      item.employee_name?.toLowerCase().includes(q) ||
      item.worker_number?.toLowerCase().includes(q) ||
      item.department_name?.toLowerCase().includes(q)
    );
  });

  return (
    <div className="min-h-screen bg-background" data-testid="pending-approvals-page">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="border-b border-border bg-card px-6 py-5">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground" data-testid="pending-approvals-title">
              Pending Leave Approvals
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Review and action all submitted leave requests
            </p>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm border border-border hover:bg-muted transition"
            data-testid="refresh-approvals-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={1.75} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        {/* ── Action feedback ─────────────────────────────────────────────── */}
        {actionSuccess && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-3 text-sm flex items-center gap-2"
               data-testid="action-success-banner">
            <CheckCircle2 className="h-4 w-4 shrink-0" />
            {actionSuccess}
          </div>
        )}
        {actionError && (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-800 px-4 py-3 text-sm flex items-center gap-2"
               data-testid="action-error-banner">
            <XCircle className="h-4 w-4 shrink-0" />
            {actionError}
          </div>
        )}

        {/* ── Stats strip ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="approvals-stats-strip">
          <StatCard icon={Clock}        label="Pending"          value={stats?.pending_count}    color="amber"
                    sub="awaiting decision" />
          <StatCard icon={CheckCircle2} label="Approved Today"   value={stats?.approved_today}   color="emerald" />
          <StatCard icon={XCircle}      label="Rejected Today"   value={stats?.rejected_today}   color="red" />
          <StatCard icon={TrendingUp}   label="Avg Pending (days)" value={stats?.avg_pending_days} color="slate"
                    sub="current queue age" />
        </div>

        {/* ── Filters ─────────────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="approvals-filter-bar">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search employee, number…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-xl border border-border bg-muted/30 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="approvals-search-input"
            />
          </div>

          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={filterDept}
              onChange={e => setFilterDept(e.target.value)}
              className="pl-8 pr-8 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
              data-testid="approvals-filter-dept"
            >
              <option value="">All Departments</option>
              {filters.departments.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          </div>

          <div className="relative">
            <select
              value={filterLt}
              onChange={e => setFilterLt(e.target.value)}
              className="pl-3 pr-8 py-2 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary appearance-none"
              data-testid="approvals-filter-leavetype"
            >
              <option value="">All Leave Types</option>
              {filters.leave_types.map(lt => (
                <option key={lt.code} value={lt.code}>{lt.name}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          </div>
        </div>

        {/* ── Error / Loading ─────────────────────────────────────────────── */}
        {error && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive flex items-center gap-2"
               data-testid="approvals-error">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {/* ── Pending Queue Table ──────────────────────────────────────────── */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground" data-testid="approvals-loading">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            Loading pending requests…
          </div>
        ) : displayed.length === 0 ? (
          <div className="rounded-xl border border-border bg-card text-center py-20 text-muted-foreground"
               data-testid="approvals-empty">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No pending approvals</p>
            <p className="text-sm mt-1 opacity-70">All leave requests have been actioned</p>
          </div>
        ) : (
          <div className="rounded-2xl border border-border overflow-hidden" data-testid="approvals-table">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-4 py-3">Employee</th>
                    <th className="text-left px-4 py-3">Department</th>
                    <th className="text-left px-4 py-3">Leave Type</th>
                    <th className="text-left px-4 py-3">Dates</th>
                    <th className="text-right px-4 py-3">Days</th>
                    <th className="text-left px-4 py-3">Balance</th>
                    <th className="text-right px-4 py-3">Pending</th>
                    <th className="text-right px-4 py-3 pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {displayed.map(item => (
                    <tr key={item.request_id}
                        className="hover:bg-muted/30 transition-colors"
                        data-testid={`approval-row-${item.request_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{item.employee_name}</div>
                        <div className="text-xs text-muted-foreground">{item.worker_number}</div>
                        {item.reason && (
                          <div className="text-xs text-muted-foreground/70 mt-0.5 max-w-[180px] truncate" title={item.reason}>
                            "{item.reason}"
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{item.department_name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                          {item.leave_type_code}
                        </span>
                        <div className="text-xs text-muted-foreground">{item.leave_type_name}</div>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        <div>{fmtDate(item.from_date)}</div>
                        <div className="text-xs">to {fmtDate(item.to_date)}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-foreground">
                        {item.requested_days}
                      </td>
                      <td className="px-4 py-3">
                        <BalancePill entitled={item.entitled_days} consumed={item.consumed_days} available={item.available_days} />
                      </td>
                      <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                        {item.days_pending > 0 ? (
                          <span className={item.days_pending >= 3 ? "text-red-600 font-medium" : ""}>
                            {pluralDays(item.days_pending)}
                          </span>
                        ) : "Today"}
                        <div className="text-[10px] opacity-60">{fmtDt(item.submitted_at)}</div>
                      </td>
                      <td className="px-4 py-3 pr-6">
                        <div className="flex gap-1.5 justify-end">
                          <button
                            onClick={() => handleApprove(item.request_id)}
                            disabled={actionLoading}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition"
                            data-testid={`approve-btn-${item.request_id}`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Approve
                          </button>
                          <button
                            onClick={() => setRejectTarget(item)}
                            disabled={actionLoading}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50 transition"
                            data-testid={`reject-btn-${item.request_id}`}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-border">
              {displayed.map(item => (
                <div key={item.request_id} className="p-4 space-y-3"
                     data-testid={`approval-card-${item.request_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-foreground">{item.employee_name}</div>
                      <div className="text-xs text-muted-foreground">{item.worker_number} · {item.department_name || "—"}</div>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                      {item.leave_type_code}
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span><CalendarDays className="inline h-3.5 w-3.5 mr-0.5" />{fmtDate(item.from_date)} – {fmtDate(item.to_date)}</span>
                    <span className="font-semibold text-foreground">{item.requested_days}d</span>
                  </div>
                  <BalancePill entitled={item.entitled_days} consumed={item.consumed_days} available={item.available_days} />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleApprove(item.request_id)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition"
                      data-testid={`mobile-approve-btn-${item.request_id}`}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Approve
                    </button>
                    <button
                      onClick={() => setRejectTarget(item)}
                      disabled={actionLoading}
                      className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl text-sm font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50 transition"
                      data-testid={`mobile-reject-btn-${item.request_id}`}
                    >
                      <XCircle className="h-4 w-4" /> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Department breakdown */}
        {stats?.by_department?.length > 0 && (
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl border border-border bg-card p-4" data-testid="dept-breakdown">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                By Department
              </h3>
              <div className="space-y-1.5">
                {stats.by_department.map(d => (
                  <div key={d.department} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground truncate">{d.department}</span>
                    <span className="font-semibold ml-2 tabular-nums">{d.pending}</span>
                  </div>
                ))}
              </div>
            </div>
            {stats?.by_leave_type?.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-4" data-testid="leavetype-breakdown">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  By Leave Type
                </h3>
                <div className="space-y-1.5">
                  {stats.by_leave_type.map(lt => (
                    <div key={lt.leave_type} className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{lt.leave_type}</span>
                      <span className="font-semibold ml-2 tabular-nums">{lt.pending}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Reject Modal ──────────────────────────────────────────────────────── */}
      {rejectTarget && (
        <RejectModal
          request={rejectTarget}
          onConfirm={handleRejectConfirm}
          onCancel={() => setRejectTarget(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
